import { api } from '@/lib/http-client';
import type { QueryParams } from '@/types/api';
import type {
  Campaign,
  CampaignFiltros,
  CampaignListResult,
  CreateCampaignRequest,
  DescubrirCampaignsResult,
  UpdateCampaignRequest,
} from '@/types/campaigns';

/**
 * Módulo 10 — Campañas/anuncios de Meta y su gasto (`docs/openapi.yaml` → tag `Campaigns`).
 *
 * Rutas reales del backend (`backend/src/modules/campaigns/campaigns.routes.ts`): el listado y el
 * detalle los puede ver cualquier miembro del negocio (`requireTenant`); crear, editar, borrar y
 * descubrir son solo ADMIN (`requireRole('ADMIN')`).
 *
 * Tres cosas del contrato que no se pueden adivinar:
 *  - el listado NO devuelve un array pelado: trae `{ items, total, gastoTotal }`, y el backend SÍ
 *    filtra (`activo`, `buscar`, `metaAdId`, `metaCampaignId` quedan en la query key);
 *  - `POST .../campaigns/descubrir` es una ruta FIJA que va antes de `/:campaignId` en Express
 *    (por eso `descubrir` no se puede confundir con un id) y es idempotente: devuelve
 *    `{ creadas, existentes, anunciosVistos, campaniasVistas, items }`;
 *  - el `DELETE` no devuelve un booleano como en Productos: responde `{ eliminada, campaignId }`.
 */

/**
 * Filtros → query params, sin mandar los que no van.
 *
 * `activo` viaja como `'true'` / `'false'`: en la query el backend lo valida con
 * `z.enum(['true', 'false'])` (`campaigns.schemas.ts`), no con una coerción booleana (un `'false'`
 * de query string se leería como `true`). El mismo objeto formado acá es el que entra en la query
 * key, así «filtro distinto» = «entrada de caché distinta».
 */
export function campaignsQueryFrom(filters: CampaignFiltros = {}): QueryParams {
  const query: QueryParams = {};

  if (filters.activo) query.activo = filters.activo;
  if (filters.buscar) query.buscar = filters.buscar;
  if (filters.metaAdId) query.metaAdId = filters.metaAdId;
  if (filters.metaCampaignId) query.metaCampaignId = filters.metaCampaignId;

  return query;
}

/**
 * `GET /api/tenants/:tenantId/campaigns` — filas de gasto + `total` + `gastoTotal`.
 *
 * Ordena por `createdAt` descendente (las últimas cargadas primero) y no pagina: el catálogo de
 * gasto de un negocio es corto y se lee entero.
 */
export function listCampaigns(
  tenantId: string,
  filters: CampaignFiltros = {},
): Promise<CampaignListResult> {
  return api.get<CampaignListResult>(`/api/tenants/${tenantId}/campaigns`, {
    query: campaignsQueryFrom(filters),
  });
}

/** `GET /api/tenants/:tenantId/campaigns/:campaignId` — 404 si no es de este negocio. */
export function fetchCampaign(tenantId: string, campaignId: string): Promise<Campaign> {
  return api.get<Campaign>(`/api/tenants/${tenantId}/campaigns/${campaignId}`);
}

/** `POST /api/tenants/:tenantId/campaigns` (solo ADMIN) — responde 201 con la fila creada. */
export function createCampaign(
  tenantId: string,
  payload: CreateCampaignRequest,
): Promise<Campaign> {
  return api.post<Campaign>(`/api/tenants/${tenantId}/campaigns`, payload);
}

/** `PATCH /api/tenants/:tenantId/campaigns/:campaignId` (solo ADMIN). */
export function updateCampaign(
  tenantId: string,
  campaignId: string,
  payload: UpdateCampaignRequest,
): Promise<Campaign> {
  return api.patch<Campaign>(`/api/tenants/${tenantId}/campaigns/${campaignId}`, payload);
}

/** `DELETE /api/tenants/:tenantId/campaigns/:campaignId` (solo ADMIN). */
export function deleteCampaign(
  tenantId: string,
  campaignId: string,
): Promise<{ eliminada: boolean; campaignId: string }> {
  return api.delete<{ eliminada: boolean; campaignId: string }>(
    `/api/tenants/${tenantId}/campaigns/${campaignId}`,
  );
}

/**
 * `POST /api/tenants/:tenantId/campaigns/descubrir` (solo ADMIN) — ruta FIJA, sin body.
 *
 * Crea con gasto 0 las filas que falten para los `ad_id` / `campaign_id` que ya aparecieron en las
 * conversaciones (el referral del webhook), con un nombre provisorio («Anuncio <id>» / «Campaña
 * <id>»). Es idempotente: repetirlo solo devuelve el resumen y el catálogo actualizado.
 */
export function discoverCampaigns(tenantId: string): Promise<DescubrirCampaignsResult> {
  return api.post<DescubrirCampaignsResult>(`/api/tenants/${tenantId}/campaigns/descubrir`);
}
